/**
 * @module stores/useStore
 * @description Main Zustand application store for EmberPOS.
 * Centralizes all client-side state: authentication, shift tracking, table management,
 * per-table order items, order lifecycle, toast notifications, UI state, metrics,
 * reservations, user settings, and sample workspace state. Persists auth, shift, settings, and
 * active table orders to localStorage for offline support.
 *
 * @example
 * import useStore, { useCurrentUser, useTableActions } from '../stores/useStore';
 * const user = useCurrentUser();
 * const { setTables } = useTableActions();
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { clearStaffToken } from '../lib/authToken';
import { apiFetch } from '../lib/api';

// ============================================
// MAIN APPLICATION STORE
// ============================================

const useStore = create(
    persist(
        (set, get) => ({
            // ============================================
            // AUTH STATE
            // ============================================
            currentUser: null,
            isAuthenticated: false,
            sessionToken: null,

            /**
             * Log in a user and set authentication state.
             * @param {Object} user - User object with id, name, role, etc.
             * @param {string|null} [token=null] - Session token from the server
             */
            login: (user, token = null) => {
                set({
                    currentUser: user,
                    isAuthenticated: true,
                    sessionToken: token
                });
            },

            /**
             * Log out the current user and clear auth/shift state. Drops the
             * staff session token only; the device stays enrolled.
             */
            logout: () => {
                clearStaffToken();
                set({
                    currentUser: null,
                    isAuthenticated: false,
                    sessionToken: null,
                    activeShift: null
                });
            },

            /**
             * The server rejected the staff session (401, e.g. the 12 h token
             * expired): drop it and go back to PIN entry. The device token
             * stays, so the same restaurant's staff can sign straight back in.
             */
            expireStaffSession: () => {
                clearStaffToken();
                set({
                    currentUser: null,
                    isAuthenticated: false,
                    sessionToken: null,
                    activeShift: null,
                    selectedTable: null,
                    view: 'login'
                });
            },

            // ============================================
            // SHIFT STATE
            // ============================================
            activeShift: null,

            /**
             * Set the active shift directly.
             * @param {Object|null} shift - Shift data object or null
             */
            setActiveShift: (shift) => set({ activeShift: shift }),

            /**
             * Record a clock-in by setting the active shift.
             * @param {Object} shiftData - Shift data from the clock-in API response
             */
            clockIn: (shiftData) => {
                set({ activeShift: shiftData });
            },

            /**
             * Record a clock-out by clearing the active shift.
             */
            clockOut: () => {
                set({ activeShift: null });
            },

            /**
             * Start a break on the current active shift.
             * Sets status to 'on_break' and records the break start time.
             */
            startBreak: () => {
                const shift = get().activeShift;
                if (shift) {
                    set({
                        activeShift: {
                            ...shift,
                            status: 'on_break',
                            currentBreakStart: new Date().toISOString()
                        }
                    });
                }
            },

            /**
             * End a break on the current active shift.
             * Calculates break duration and accumulates total break minutes.
             */
            endBreak: () => {
                const shift = get().activeShift;
                if (shift && shift.currentBreakStart) {
                    const breakDuration = (new Date() - new Date(shift.currentBreakStart)) / 1000 / 60;
                    set({
                        activeShift: {
                            ...shift,
                            status: 'active',
                            currentBreakStart: null,
                            totalBreakMinutes: (shift.totalBreakMinutes || 0) + breakDuration
                        }
                    });
                }
            },

            // ============================================
            // TABLES STATE
            // ============================================
            tables: [],
            selectedTable: null,

            /**
             * Replace the entire tables array.
             * @param {Array<Object>} tables - New array of table objects
             */
            setTables: (tables) => set({ tables }),

            /**
             * Update a single table by ID with partial data.
             * @param {number|string} tableId - Table identifier
             * @param {Object} updates - Partial table object to merge
             */
            updateTable: (tableId, updates) => {
                const tables = get().tables.map(t =>
                    t.id === tableId ? { ...t, ...updates } : t
                );
                set({ tables });
            },

            /**
             * Select a table by ID for the order entry view.
             * @param {number|string} tableId - Table identifier
             */
            selectTable: (tableId) => {
                const table = get().tables.find(t => t.id === tableId);
                set({ selectedTable: table || null });
            },

            /** Clear the currently selected table. */
            clearSelectedTable: () => set({ selectedTable: null }),

            // ============================================
            // ACTIVE TABLE ORDERS (Persistent per-table order items)
            // ============================================
            activeTableOrders: {}, // { [tableId]: { items: [], guestProfiles: {}, lastUpdated: timestamp } }

            /**
             * Get the current order items for a specific table.
             * @param {number|string} tableId - Table identifier
             * @returns {Array<Object>} Array of order item objects
             */
            getTableOrder: (tableId) => {
                return get().activeTableOrders[tableId]?.items || [];
            },

            /**
             * Get guest profiles for a specific table.
             * @param {number|string} tableId - Table identifier
             * @returns {Object} Guest profiles keyed by seat/guest identifier
             */
            getTableGuests: (tableId) => {
                return get().activeTableOrders[tableId]?.guestProfiles || {};
            },

            /**
             * Set the entire order for a table (items and optionally guest profiles).
             * @param {number|string} tableId - Table identifier
             * @param {Array<Object>} items - Complete array of order items
             * @param {Object|null} [guestProfiles=null] - Guest profiles, or null to keep existing
             */
            setTableOrder: (tableId, items, guestProfiles = null) => {
                const current = get().activeTableOrders[tableId] || {};
                set({
                    activeTableOrders: {
                        ...get().activeTableOrders,
                        [tableId]: {
                            items,
                            guestProfiles: guestProfiles !== null ? guestProfiles : (current.guestProfiles || {}),
                            lastUpdated: Date.now()
                        }
                    }
                });
            },

            /**
             * Add a single item to a table's order.
             * @param {number|string} tableId - Table identifier
             * @param {Object} item - Order item to add (uniqueId auto-assigned if missing)
             */
            addItemToTableOrder: (tableId, item) => {
                const currentItems = get().activeTableOrders[tableId]?.items || [];
                const currentGuests = get().activeTableOrders[tableId]?.guestProfiles || {};
                set({
                    activeTableOrders: {
                        ...get().activeTableOrders,
                        [tableId]: {
                            items: [...currentItems, { ...item, uniqueId: item.uniqueId || Date.now() }],
                            guestProfiles: currentGuests,
                            lastUpdated: Date.now()
                        }
                    }
                });
            },

            /**
             * Update an existing item in a table's order by uniqueId.
             * @param {number|string} tableId - Table identifier
             * @param {number|string} uniqueId - The item's unique identifier
             * @param {Object} updates - Partial item object to merge
             */
            updateTableOrderItem: (tableId, uniqueId, updates) => {
                const currentItems = get().activeTableOrders[tableId]?.items || [];
                const currentGuests = get().activeTableOrders[tableId]?.guestProfiles || {};
                const updatedItems = currentItems.map(item =>
                    item.uniqueId === uniqueId ? { ...item, ...updates } : item
                );
                set({
                    activeTableOrders: {
                        ...get().activeTableOrders,
                        [tableId]: {
                            items: updatedItems,
                            guestProfiles: currentGuests,
                            lastUpdated: Date.now()
                        }
                    }
                });
            },

            /**
             * Remove an item from a table's order by uniqueId.
             * @param {number|string} tableId - Table identifier
             * @param {number|string} uniqueId - The item's unique identifier
             */
            removeTableOrderItem: (tableId, uniqueId) => {
                const currentItems = get().activeTableOrders[tableId]?.items || [];
                const currentGuests = get().activeTableOrders[tableId]?.guestProfiles || {};
                set({
                    activeTableOrders: {
                        ...get().activeTableOrders,
                        [tableId]: {
                            items: currentItems.filter(item => item.uniqueId !== uniqueId),
                            guestProfiles: currentGuests,
                            lastUpdated: Date.now()
                        }
                    }
                });
            },

            /**
             * Clear all order items for a table (after payment/close).
             * @param {number|string} tableId - Table identifier
             */
            clearTableOrder: (tableId) => {
                const orders = { ...get().activeTableOrders };
                delete orders[tableId];
                set({ activeTableOrders: orders });
            },

            /**
             * Update guest profiles for a table.
             * @param {number|string} tableId - Table identifier
             * @param {Object} guestProfiles - Guest profiles keyed by seat/guest identifier
             */
            setTableGuests: (tableId, guestProfiles) => {
                const current = get().activeTableOrders[tableId] || { items: [] };
                set({
                    activeTableOrders: {
                        ...get().activeTableOrders,
                        [tableId]: {
                            ...current,
                            guestProfiles,
                            lastUpdated: Date.now()
                        }
                    }
                });
            },

            // ============================================
            // ORDERS STATE
            // ============================================
            orders: [],
            currentOrder: null,

            // ============================================
            // MENU ITEMS STATE
            // ============================================
            menuItems: [],

            /**
             * Replace the entire menuItems array.
             * @param {Array<Object>} items - New array of menu item objects
             */
            setMenuItems: (items) => set({ menuItems: items }),

            /**
             * Re-fetch menu items from server and update store.
             * Called after auto-86 events to keep UI in sync.
             */
            refreshMenuItems: async () => {
                try {
                    const res = await apiFetch(`/api/menu/items`);
                    if (res.ok) {
                        const data = await res.json();
                        // data may be { categories: [...] } or flat array
                        const items = Array.isArray(data)
                            ? data
                            : (data.categories ? data.categories.flatMap(c => c.items || []) : []);
                        set({ menuItems: items });
                    }
                } catch (e) {
                    console.error('[Store] refreshMenuItems failed:', e.message);
                }
            },

            /**
             * Replace the entire orders array.
             * @param {Array<Object>} orders - New array of order objects
             */
            setOrders: (orders) => set({ orders }),

            /**
             * Append a new order to the orders array.
             * @param {Object} order - Order object to add
             */
            addOrder: (order) => {
                set({ orders: [...get().orders, order] });
            },

            /**
             * Update a specific order by ID with partial data.
             * Also updates currentOrder if it matches.
             * @param {string|number} orderId - Order identifier
             * @param {Object} updates - Partial order object to merge
             */
            updateOrder: (orderId, updates) => {
                const orders = get().orders.map(o =>
                    o.id === orderId ? { ...o, ...updates } : o
                );
                set({ orders });

                // Also update currentOrder if it's the one being updated
                const current = get().currentOrder;
                if (current && current.id === orderId) {
                    set({ currentOrder: { ...current, ...updates } });
                }
            },

            /**
             * Remove an order by ID from the orders array.
             * @param {string|number} orderId - Order identifier
             */
            removeOrder: (orderId) => {
                set({ orders: get().orders.filter(o => o.id !== orderId) });
            },

            /**
             * Set the currently focused order for detail views.
             * @param {Object|null} order - Order object or null
             */
            setCurrentOrder: (order) => set({ currentOrder: order }),

            /** Clear the currently focused order. */
            clearCurrentOrder: () => set({ currentOrder: null }),

            /**
             * Get all orders that are in kitchen/sent status for display.
             * @returns {Array<Object>} Array of kitchen-eligible orders
             */
            getKitchenOrders: () => {
                return get().orders.filter(o =>
                    ['sent', 'fired', 'ready'].includes(o.status)
                ).sort((a, b) =>
                    new Date(a.sentToKitchenAt || a.createdAt) - new Date(b.sentToKitchenAt || b.createdAt)
                );
            },

            // ============================================
            // NOTIFICATIONS / TOASTS
            // ============================================
            notifications: [],

            /**
             * Add a toast notification. Auto-removes after the specified duration.
             * @param {Object} notification - Notification config
             * @param {string} notification.message - Display message
             * @param {string} [notification.type='info'] - Type: 'info', 'success', 'error', 'warning'
             * @param {number} [notification.duration=4000] - Auto-dismiss time in ms (0 for persistent)
             * @returns {number} The notification ID
             */
            addNotification: (notification) => {
                const id = Date.now();
                const newNotification = {
                    id,
                    type: 'info',
                    duration: 4000,
                    ...notification
                };

                set({ notifications: [...get().notifications, newNotification] });

                // Auto-remove after duration
                if (newNotification.duration > 0) {
                    setTimeout(() => {
                        get().removeNotification(id);
                    }, newNotification.duration);
                }

                return id;
            },

            /**
             * Remove a notification by ID.
             * @param {number} id - Notification identifier
             */
            removeNotification: (id) => {
                set({ notifications: get().notifications.filter(n => n.id !== id) });
            },

            /** Clear all notifications. */
            clearNotifications: () => set({ notifications: [] }),

            /** Convenience methods for common notification types. */
            notify: {
                /** @param {string} message @param {Object} [options] */
                success: (message, options = {}) =>
                    get().addNotification({ message, type: 'success', ...options }),
                /** @param {string} message @param {Object} [options] */
                error: (message, options = {}) =>
                    get().addNotification({ message, type: 'error', duration: 6000, ...options }),
                /** @param {string} message @param {Object} [options] */
                warning: (message, options = {}) =>
                    get().addNotification({ message, type: 'warning', ...options }),
                /** @param {string} message @param {Object} [options] */
                info: (message, options = {}) =>
                    get().addNotification({ message, type: 'info', ...options }),
            },

            // ============================================
            // UI STATE
            // ============================================
            view: 'launcher',
            isLoading: false,
            sidebarOpen: false,

            /**
             * Navigate to a different view.
             * @param {string} view - View name (e.g. 'launcher', 'floor', 'kitchen', 'order')
             */
            setView: (view) => set({ view }),

            /**
             * Set the global loading state.
             * @param {boolean} isLoading - Whether the app is in a loading state
             */
            setLoading: (isLoading) => set({ isLoading }),

            /** Toggle the sidebar open/closed. */
            toggleSidebar: () => set({ sidebarOpen: !get().sidebarOpen }),

            // ============================================
            // METRICS STATE (for Manager Dashboard)
            // ============================================
            metrics: {
                sales: 0,
                laborCost: 0,
                laborPercentage: 0,
                covers: 0,
                averageCheck: 0,
                target: 22.0
            },

            /**
             * Update metrics with partial data (merges with existing).
             * @param {Object} metrics - Partial metrics object
             */
            setMetrics: (metrics) => set({ metrics: { ...get().metrics, ...metrics } }),

            // ============================================
            // RESERVATIONS STATE
            // ============================================
            reservations: [],

            /**
             * Replace the entire reservations array.
             * @param {Array<Object>} reservations - New array of reservation objects
             */
            setReservations: (reservations) => set({ reservations }),

            /**
             * Update a specific reservation by ID with partial data.
             * @param {string} reservationId - Reservation identifier
             * @param {Object} updates - Partial reservation object to merge
             */
            updateReservation: (reservationId, updates) => {
                const reservations = get().reservations.map(r =>
                    r.id === reservationId ? { ...r, ...updates } : r
                );
                set({ reservations });
            },

            // ============================================
            // SETTINGS
            // ============================================
            settings: {
                soundEnabled: true,
                soundVolume: 0.7,
                darkMode: false,
                compactMode: false
            },

            /**
             * Update user settings with partial data (merges with existing).
             * @param {Object} updates - Partial settings object
             */
            updateSettings: (updates) => {
                set({ settings: { ...get().settings, ...updates } });
            },

            // ============================================
            // SAMPLE WORKSPACE STATE
            // ============================================
            isDemoMode: true,
            demoScenario: null,

            /**
             * Set the active demo scenario.
             * @param {string|null} scenario - Scenario identifier or null
             */
            setDemoScenario: (scenario) => set({ demoScenario: scenario }),

            /**
             * Reset all state to initial defaults (full demo reset).
             */
            resetDemo: () => {
                clearStaffToken();
                set({
                    currentUser: null,
                    isAuthenticated: false,
                    sessionToken: null,
                    activeShift: null,
                    tables: [],
                    orders: [],
                    currentOrder: null,
                    selectedTable: null,
                    reservations: [],
                    metrics: {
                        sales: 0,
                        laborCost: 0,
                        laborPercentage: 0,
                        covers: 0,
                        averageCheck: 0,
                        target: 22.0
                    },
                    view: 'launcher',
                    notifications: [],
                    demoScenario: null
                });
            }
        }),
        {
            name: 'fire-pos-storage',
            version: 2, // Increment for schema changes
            partialize: (state) => ({
                // Only persist these fields
                currentUser: state.currentUser,
                isAuthenticated: state.isAuthenticated,
                sessionToken: state.sessionToken,
                activeShift: state.activeShift,
                settings: state.settings,
                // Persist active table orders for offline support
                activeTableOrders: state.activeTableOrders
            }),
            migrate: (persistedState, version) => {
                if (version === 1) {
                    // Migrate from v1 to v2 - add activeTableOrders
                    return {
                        ...persistedState,
                        activeTableOrders: {}
                    };
                }
                return persistedState;
            }
        }
    )
);

// ============================================
// SELECTORS (for optimized re-renders)
// ============================================

/** @returns {Object|null} Current authenticated user */
export const useCurrentUser = () => useStore((state) => state.currentUser);
/** @returns {boolean} Whether a user is authenticated */
export const useIsAuthenticated = () => useStore((state) => state.isAuthenticated);
/** @returns {Object|null} Active shift data */
export const useActiveShift = () => useStore((state) => state.activeShift);
/** @returns {Array<Object>} All tables */
export const useTables = () => useStore((state) => state.tables);
/** @returns {Object|null} Currently selected table */
export const useSelectedTable = () => useStore((state) => state.selectedTable);
/** @returns {Array<Object>} All orders */
export const useOrders = () => useStore((state) => state.orders);
/** @returns {Object|null} Currently focused order */
export const useCurrentOrder = () => useStore((state) => state.currentOrder);
/** @returns {Array<Object>} Active toast notifications */
export const useNotifications = () => useStore((state) => state.notifications);
/** @returns {string} Current view name */
export const useView = () => useStore((state) => state.view);
/** @returns {Object} Dashboard metrics */
export const useMetrics = () => useStore((state) => state.metrics);
/** @returns {Array<Object>} Reservations */
export const useReservations = () => useStore((state) => state.reservations);
/** @returns {Object} User settings (soundEnabled, soundVolume, darkMode, compactMode) */
export const useSettings = () => useStore((state) => state.settings);
/** @returns {Array<Object>} All menu items */
export const useMenuItems = () => useStore((state) => state.menuItems);

/** @returns {{login: Function, logout: Function}} Auth action selectors */
export const useAuth = () => useStore((state) => ({
    login: state.login,
    logout: state.logout
}));

/** @returns {{setTables: Function, updateTable: Function, selectTable: Function, clearSelectedTable: Function}} Table action selectors */
export const useTableActions = () => useStore((state) => ({
    setTables: state.setTables,
    updateTable: state.updateTable,
    selectTable: state.selectTable,
    clearSelectedTable: state.clearSelectedTable
}));

/** @returns {{setOrders: Function, addOrder: Function, updateOrder: Function, removeOrder: Function, setCurrentOrder: Function, clearCurrentOrder: Function, getKitchenOrders: Function}} Order action selectors */
export const useOrderActions = () => useStore((state) => ({
    setOrders: state.setOrders,
    addOrder: state.addOrder,
    updateOrder: state.updateOrder,
    removeOrder: state.removeOrder,
    setCurrentOrder: state.setCurrentOrder,
    clearCurrentOrder: state.clearCurrentOrder,
    getKitchenOrders: state.getKitchenOrders
}));

/** @returns {Object} Notification convenience methods (success, error, warning, info) */
export const useNotify = () => useStore((state) => state.notify);

export default useStore;
